import { useCallback, useState } from 'react';
import { type FreeGiftsData, useProductGiftLazyQuery } from '../../../types/front.graphql';
import { productGiftsStore$ } from '../stores/product-gifts.store';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface UseProductGiftLazyFetchReturn {
    /** Trigger fetch for a specific SKU. Returns the gift data list. */
    fetchGiftData: (sku: string) => Promise<FreeGiftsData[]>;
    /** Loading state */
    loading: boolean;
    /** Error state */
    error: Error | undefined;
    /** Raw gifts data list (unsorted) */
    giftsDataList: FreeGiftsData[];
    /** Sorted by discount_step ascending */
    sortedGiftsDataList: FreeGiftsData[];
}

/**
 * Hook for lazy-fetching product gift data on demand.
 * Uses productGiftsStore$ as cache with 5-minute TTL.
 * Designed for PLP quick-add flow where gift data is only needed when user interacts.
 */
export const useProductGiftLazyFetch = (): UseProductGiftLazyFetchReturn => {
    const [fetchProductGift] = useProductGiftLazyQuery();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>();
    const [giftsDataList, setGiftsDataList] = useState<FreeGiftsData[]>([]);
    const [sortedGiftsDataList, setSortedGiftsDataList] = useState<FreeGiftsData[]>([]);

    const fetchGiftData = useCallback(
        async (sku: string): Promise<FreeGiftsData[]> => {
            // 1. Check productGiftsStore$ cache first
            const cached = productGiftsStore$[sku]?.peek();
            if (cached?.updatedAt) {
                const age = Date.now() - new Date(cached.updatedAt).getTime();
                if (age < CACHE_TTL_MS && cached.giftsDataList.length > 0) {
                    setGiftsDataList(cached.giftsDataList);
                    setSortedGiftsDataList(cached.sortedGiftsDataList);
                    return cached.giftsDataList;
                }
            }

            // 2. Fetch from server via Apollo lazy query
            setLoading(true);
            setError(undefined);
            try {
                const result = await fetchProductGift({
                    variables: { sku },
                    fetchPolicy: 'cache-first',
                });

                // Filter only product-level gift rules (ampromo_eachn)
                const items = (result.data?.freeGifts?.items?.filter(
                    item => item !== null && item?.rule?.simple_action === 'ampromo_eachn'
                ) ?? []) as FreeGiftsData[];

                const sorted = [...items].sort((a, b) => (a.rule?.discount_step ?? 0) - (b.rule?.discount_step ?? 0));

                // 3. Save to productGiftsStore$ cache
                productGiftsStore$[sku].set({
                    giftsDataList: items,
                    sortedGiftsDataList: sorted,
                    loading: false,
                    error: undefined,
                    updatedAt: new Date().toISOString(),
                });

                setGiftsDataList(items);
                setSortedGiftsDataList(sorted);
                return items;
            } catch (err) {
                const fetchError = err instanceof Error ? err : new Error('Failed to fetch gift data');
                setError(fetchError);
                return [];
            } finally {
                setLoading(false);
            }
        },
        [fetchProductGift]
    );

    return { fetchGiftData, loading, error, giftsDataList, sortedGiftsDataList };
};
